// RecipeKidMenuSection — parent-side affordance on the recipe page
// to mark the dish as "kids can request this." Sibling to the
// per-pantry-row toggle in MCMItemCard; writes to kid_recipes
// (mark / unmark / updateMeta) instead of pantry_items.
//
// Why inline on the recipe page: the global bulk sheet
// (Settings → Kids Mode → KID MEALS) requires the parent to leave
// the recipe and dig through Settings. Putting a toggle right next
// to COOK NOW / SCHEDULE / SHARE makes the kid-menu axis the same
// level of discoverable as those other recipe-level actions.
//
// Three controls when enabled:
//   1. Toggle — marks / unmarks the recipe for the kid menu.
//   2. Label  — kid-facing nickname (e.g. "Cheesy Pasta" for a
//               long recipe title).
//   3. Kind   — Meal / Snack / Treat chip rail, drives which kid
//               segment (Recipes / Treats) the tile lands in.

import { useEffect, useMemo, useRef, useState } from "react";
import { FONT, COLOR } from "../lib/tokens";
import { useKidRecipes } from "../lib/useKidRecipes";
import { supabase } from "../lib/supabase";
import { findIngredient } from "../data/ingredients";
import { cardsForRecipe } from "../lib/cardsForRecipe";
import CardPreviewChips from "./CardPreviewChips";

const KIND_OPTIONS = [
  { key: "meal",    label: "Meal" },
  { key: "snack",   label: "Snack" },
  { key: "dessert", label: "Treat" },
];

export default function RecipeKidMenuSection({ recipe, userId }) {
  const slug = recipe?.slug;
  const { rows, mark, unmark, updateMeta } = useKidRecipes(userId, { ownOnly: true });

  const myRow = (() => {
    if (!slug || !rows) return null;
    return rows.find((r) => r.recipe_slug === slug && r.marked_by === userId) || null;
  })();

  const enabled = !!myRow;
  const kind = myRow?.kid_kind || "meal";

  const [localLabel, setLocalLabel] = useState(myRow?.kid_label || "");
  const lastWroteRef = useRef(myRow?.kid_label || "");

  useEffect(() => {
    const v = myRow?.kid_label || "";
    if (v === lastWroteRef.current) return;
    setLocalLabel(v);
  }, [myRow?.kid_label]);

  useEffect(() => {
    if (!enabled || !slug) return undefined;
    const v = myRow?.kid_label || "";
    if (localLabel === v) return undefined;
    const id = setTimeout(async () => {
      const next = localLabel.trim() || null;
      lastWroteRef.current = next || "";
      try {
        await updateMeta({ slug, kidLabel: next, kidKind: kind });
      } catch (e) {
        console.warn("[recipe-kid-menu] updateMeta:", e?.message || e);
      }
    }, 380);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localLabel, enabled, slug, kind]);

  if (!slug || !userId) return null;

  // RecipeBook's RecipePage gets `recipe` from a user_recipes-shaped
  // row where the recipe JSON lives at `recipe.recipe.*`. Title and
  // emoji can be present at EITHER level depending on the source
  // (custom builder writes top-level, AI / imports nest). Resolve
  // both so the mark write always carries something readable.
  const resolvedTitle = recipe?.title || recipe?.recipe?.title || "";
  const resolvedEmoji = recipe?.emoji || recipe?.recipe?.emoji || null;
  const resolvedIngredients = recipe?.ingredients || recipe?.recipe?.ingredients || [];

  // Predict what cards eating this dish will give. Memoized off the
  // ingredient identity — recomputes when the recipe row swaps but
  // not on every re-render. Skipped entirely until the section is
  // enabled — no point computing for an unmarked dish.
  const prediction = useMemo(() => {
    if (!enabled || !resolvedIngredients.length) return null;
    return cardsForRecipe(
      { ingredients: resolvedIngredients },
      { getIngredient: findIngredient },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, JSON.stringify(resolvedIngredients)]);

  // Heuristic kid_kind default — cookies / cakes / candy / ice cream
  // / pies / brownies / donuts / pudding / cheesecake / candy bars
  // should land as Treat instead of the generic Meal default. The
  // parent can override via the chip rail below; we just don't want
  // to start them in the wrong bucket.
  const guessKidKind = () => {
    const text = `${resolvedTitle || ""} ${slug || ""}`.toLowerCase();
    if (/\b(cookie|cake|brownie|cupcake|cheesecake|pie|donut|doughnut|ice ?cream|gelato|sorbet|pudding|candy|chocolate|truffle|tart|macaron|macaroon|fudge|caramel|toffee|frosting|icing|sweet|dessert|treat)\b/.test(text)) {
      return "dessert";
    }
    if (/\b(snack|chip|cracker|pretzel|popcorn|trail ?mix|jerky|stick|bar|bite|nugget)\b/.test(text)) {
      return "snack";
    }
    return "meal";
  };

  const toggle = async () => {
    try {
      if (enabled) {
        await unmark(slug);
      } else {
        await mark({
          slug,
          title: resolvedTitle,
          emoji: resolvedEmoji,
          kidLabel: null,
          kidKind: guessKidKind(),
        });
        // Fire-and-forget AI emoji + voiceline generation, same
        // pattern as the canonical (kitchen item) flow. Emoji skips
        // when a real photo exists; voiceline skips when the row
        // already cached. So no double-spend on re-toggle.
        if (userId) {
          supabase.functions.invoke("generate-recipe-emoji", {
            body: {
              recipeSlug:  slug,
              recipeTitle: resolvedTitle || slug,
              userId,
              kidLabel:    myRow?.kid_label || null,
            },
          }).catch((err) => {
            console.warn("[recipe-kid-emoji] invoke failed:", err?.message || err);
          });
          supabase.functions.invoke("generate-canonical-voicelines", {
            body: {
              targetKind:   "recipe",
              canonicalId:  slug,
              kidLabel:     myRow?.kid_label || null,
              fallbackText: resolvedTitle || slug,
            },
          }).catch((err) => {
            console.warn("[recipe-kid-voicelines] invoke failed:", err?.message || err);
          });
        }
      }
    } catch (e) {
      console.warn("[recipe-kid-menu] toggle:", e?.message || e);
    }
  };

  const setKind = async (k) => {
    if (!enabled) return;
    try {
      await updateMeta({ slug, kidLabel: myRow?.kid_label || null, kidKind: k });
    } catch (e) {
      console.warn("[recipe-kid-menu] setKind:", e?.message || e);
    }
  };

  const accent = "rgba(123,156,92,0.45)"; // kid-leaf tone, same family as SHARE
  const accentFill = "rgba(123,156,92,0.18)";

  return (
    <div style={{ marginTop: 14 }}>
      <button
        type="button"
        onClick={toggle}
        style={{
          all: "unset",
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "12px 14px",
          background: enabled ? accentFill : "transparent",
          border: `1px ${enabled ? "solid" : "dashed"} ${accent}`,
          borderRadius: 10,
          cursor: "pointer",
          touchAction: "manipulation",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "left", minWidth: 0 }}>
          <div style={{
            fontFamily: FONT.forma,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: COLOR.sub,
          }}>
            {enabled ? "On kids menu" : "Add to kids menu"}
          </div>
          <div style={{
            fontFamily: FONT.forma,
            fontSize: 14,
            fontWeight: 500,
            color: COLOR.ink,
          }}>
            {enabled ? "Kids can request this recipe" : "Let kids request this recipe"}
          </div>
        </div>
        <div
          style={{
            flexShrink: 0,
            width: 26,
            height: 26,
            borderRadius: 8,
            background: enabled ? "#7B9C5C" : "transparent",
            border: `2px solid ${enabled ? "#7B9C5C" : accent}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontFamily: FONT.forma,
            fontSize: 16,
            fontWeight: 700,
          }}
        >
          {enabled ? "✓" : ""}
        </div>
      </button>

      {enabled && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
          <input
            type="text"
            value={localLabel}
            onChange={(e) => setLocalLabel(e.target.value)}
            placeholder={`What kids call it (default: ${resolvedTitle || "this recipe"})`}
            maxLength={48}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "10px 12px",
              fontFamily: FONT.forma,
              fontSize: 14,
              background: "#fff",
              border: `1px solid ${COLOR.border}`,
              borderRadius: 8,
              color: COLOR.ink,
              outline: "none",
            }}
          />
          <div style={{ display: "flex", gap: 6 }}>
            {KIND_OPTIONS.map((k) => {
              const active = kind === k.key;
              return (
                <button
                  key={k.key}
                  type="button"
                  onClick={() => setKind(k.key)}
                  style={{
                    flex: 1,
                    padding: "10px 4px",
                    fontFamily: FONT.forma,
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.10em",
                    textTransform: "uppercase",
                    color: active ? "#fff" : COLOR.ink,
                    background: active ? "#7B9C5C" : "transparent",
                    border: `1.5px solid ${accent}`,
                    borderRadius: 8,
                    cursor: "pointer",
                    touchAction: "manipulation",
                  }}
                >
                  {k.label}
                </button>
              );
            })}
          </div>
          <CardPreviewChips prediction={prediction} />
        </div>
      )}
    </div>
  );
}
